import { Injectable, inject, signal } from '@angular/core';
import {  of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { WorkflowService } from '../data/workflow.service';
import { PaginationService } from '../base/pagination.service';
import { WorkflowProjectTableItem } from '../../models/Workflow/WorkflowProjectTableItem';
import { CLOSING_FIELD_CODE } from '../../data';

export const SORT_FIELDS = [
  { value: 'Id', label: 'ID' },
  { value: 'ODT', label: 'Folio' },
  { value: 'LocationKey', label: 'CURP' },
  { value: 'WorkFlowProjectState', label: 'Estatus' },
  { value: 'StartDate', label: 'Fecha Inicio' },
  { value: 'ClosingDate', label: 'Fecha Cierre' },
] as const;

export const SORT_DIRECTIONS = [
  { value: 'Asc', label: 'Ascendente' },
  { value: 'Desc', label: 'Descendente' },
] as const;

@Injectable({ providedIn: 'root' })
export class TicketListStore {
  private readonly workflowService = inject(WorkflowService);
  private readonly paginationService = inject(PaginationService);
  private readonly CLOSING_FIELDS_CODES_TO_INCLUDE = [Number(CLOSING_FIELD_CODE.TICKET_STATUS)];

  projects = signal<WorkflowProjectTableItem[]>([]);
  isLoading = signal(false);

  sortField = signal<string>('ODT');
  sortDirection = signal<string>('Desc');

  pagination = this.paginationService;

  private readonly DEFAULT_SEARCH_FIELD = 'WorkFlowType';
  private readonly DEFAULT_SEARCH_VALUE = 'Registro';

  load(): void {
    this.isLoading.set(true);

    this.workflowService.getWorkFlowProjectsPaged({
      page: this.pagination.page(),
      pageSize: this.pagination.pageSize(),
      sort: this.sortField(),
      sortDir: this.sortDirection(),
      searchField: this.DEFAULT_SEARCH_FIELD,
      searchFieldValue: this.DEFAULT_SEARCH_VALUE,
      includedClosingFields: this.CLOSING_FIELDS_CODES_TO_INCLUDE,
    }).pipe(
      switchMap(response => {
        if (!response?.Data?.Items?.length) {
          this.isLoading.set(false);
          return of({ items: [] as WorkflowProjectTableItem[], total: 0 });
        }
        const items: WorkflowProjectTableItem[] = response.Data.Items.map((project, i) => {
          return {
            id: project.Id,
            odt: project.ODT,
            workflow: project.WorkFlow,
            telefono: '',
            direccion: project.Address || '',
            curp: project.LocationKey || '',
            locationKey: project.LocationKey,
            status: project.ClosingFields?.find(field => field.Code === Number(CLOSING_FIELD_CODE.TICKET_STATUS))?.Value || 'En Proceso',
            startDate: project.StartDate || '',
          };
        });
        return of({ items, total: response.Data.TotalCount });

      }),
      catchError(() => {
        this.isLoading.set(false);
        return of({ items: [] as WorkflowProjectTableItem[], total: 0 });
      })
    ).subscribe({
      next: result => {
        this.projects.set(result.items);
        this.pagination.setTotal(result.total);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  setSortField(field: string): void {
    this.sortField.set(field);
    this.pagination.setPage(0);
    this.load();
  }

  setSortDirection(direction: string): void {
    this.sortDirection.set(direction);
    this.pagination.setPage(0);
    this.load();
  }

  reload(): void {
    this.load();
  }
}
